import api, { publicProposalApi } from '@global-apis/config';
import CoreIntegration from '@global-interfaces/CoreIntegration';
import Project, { ProjectSettings } from '@global-interfaces/Project';
import Vendor from '@global-interfaces/Vendor';
import Address from '@global-interfaces/Address';

export type SensitiveBankAction = 'bank_destination_create' | 'bank_destination_replace' | 'bank_metadata_update';

export interface BankChangePayload {
  account_number: string;
  routing_number: string;
  account_type?: 'checking' | 'savings';
  account_name?: string;
  institution_name?: string;
}

export interface BankProposalSummary {
  id: number;
  vendor: Pick<Vendor, 'id' | 'name' | 'company_id' | 'bank_id' | 'address_id'>;
  state: 'pending' | 'approved_awaiting_provider' | 'provider_unknown';
  can_reconcile: boolean;
  account_last_four: string;
  routing_last_four: string;
  account_type: string;
  account_name: string;
  institution_name: string;
  expires_at: string;
}

interface StepUpScope {
  action_type: SensitiveBankAction;
  target_identifier?: string;
}

interface StepUpIssueScope extends StepUpScope {
  bank_change?: BankChangePayload;
}

let proposalCsrfToken: string | null = null;

const proposalCsrf = async () => {
  if (proposalCsrfToken) {
    return proposalCsrfToken;
  }
  const response = await publicProposalApi.get<{ csrf_token: string }>('registration/vendor-proposal/csrf/');
  proposalCsrfToken = response.data.csrf_token;
  return proposalCsrfToken;
};

async function proposalPost<T>(path: string, data: unknown) {
  const csrfToken = await proposalCsrf();
  return publicProposalApi.post<T>(path, data, {
    withCredentials: true,
    headers: { 'X-CSRFToken': csrfToken }
  });
}

export interface ProjectToSendValues {
  name?: string;
  billing_due_date?: number;
  billing_frequency?: string;
  active?: boolean;
  tax_rate?: number;
  address?: {
    address_1?: string;
    city?: string;
    zip_code?: string;
    state?: string;
  };
  owner?: string;
  sage_id?: string;
  expense_account?: string;
  project_settings?: ProjectSettings;
  external_data?: {
    qbd_full_name?: string;
    qbo?: {
      job_name: string;
    };
    [key: string]: unknown;
  };
}

interface VendorForm extends Omit<Vendor, 'address'> {
  address: Partial<Address>;
}

const companiesAPI = {
  integrations: {
    create: async (data: Partial<CoreIntegration>) => api.post<CoreIntegration>('integrations/', data),
    partialUpdate: async (id: number, data: Partial<CoreIntegration>) =>
      api.patch<Partial<CoreIntegration>>(`integrations/${id}/`, data),
    update: async (id: number, data: { sage_host: string }) => api.put<void>(`/integrations/${id}/`, data)
  },
  vendor: {
    bank: {
      fetch: async (vendorId: number) => api.get(`v4/vendors/${vendorId}/bank/`),
      // biome-ignore lint/suspicious/noExplicitAny: safe to ignore
      create: async (vendorId: number, data: any) => api.post(`v4/vendors/${vendorId}/bank/`, data),
      // biome-ignore lint/suspicious/noExplicitAny: safe to ignore
      update: async (vendorId: number, data: any) => api.put(`v4/vendors/${vendorId}/bank/`, data)
    },
    create: async (data: Partial<VendorForm>) => api.post<Vendor>('vendors/', data),
    fetch: async (id: number) => api.get<Vendor>(`vendors/${id}/`).then((res) => res),
    update: async (id: number, data: Partial<VendorForm>) => api.patch(`vendors/${id}/`, data),
    bankChange: async (id: number, data: BankChangePayload & { challenge: string }) =>
      api.post(`vendors/${id}/bank-change/`, data),
    stepUp: {
      issue: async (id: number, scope: StepUpIssueScope) =>
        api.post<{ challenge: string; challenge_id: number; expires_at: string; action_type: SensitiveBankAction }>(
          `vendors/${id}/bank-change/challenge/`,
          scope
        ),
      verify: async (id: number, data: StepUpScope & { challenge: string; otp: string }) =>
        api.post<{ challenge_id: number; verified_at: string }>(`vendors/${id}/bank-change/challenge/verify/`, data)
    },
    proposal: {
      fetch: async (id: number) => api.get<BankProposalSummary | undefined>(`vendors/${id}/bank-proposal/`),
      approve: async (id: number, proposalId: number, challenge: string) =>
        api.post<{ id: number; state: string }>(`vendors/${id}/bank-proposal/approve/`, {
          proposal_id: proposalId,
          challenge
        }),
      deny: async (id: number, proposalId: number, reason: string) =>
        api.post<{ id: number; state: string }>(`vendors/${id}/bank-proposal/deny/`, {
          proposal_id: proposalId,
          reason
        }),
      reconcile: async (id: number, proposalId: number, challenge: string) =>
        api.post<{ id: number; state: string }>(`vendors/${id}/bank-proposal/reconcile/`, {
          proposal_id: proposalId,
          challenge
        })
    },
    // biome-ignore lint/suspicious/noExplicitAny: safe to ignore
    invite: async (id: number, data: any) => api.post(`vendors/${id}/invite/`, data),
    getInviteToken: async (id: number) => api.post<{ token: string }>(`vendors/${id}/invite/token/`, {}),
    submitBankProposal: async (data: BankChangePayload) =>
      proposalPost<{ proposal_id: number; state: 'pending'; message_code: 'pending_internal_approval' }>(
        'registration/vendor-proposal/',
        data
      ),
    fetchProposalSession: async () =>
      publicProposalApi.get<{ vendor_id: number; name: string; email: string; proposal_session: true }>(
        'registration/vendor-proposal/',
        { withCredentials: true }
      ),
    exchangeProposalBootstrap: async (bootstrapToken: string) =>
      proposalPost<{ vendor_id: number; name: string; email: string; proposal_session: true }>(
        'registration/vendor-proposal/exchange/',
        { bootstrap_token: bootstrapToken }
      ),
    delete: async (id: number) => api.delete<void>(`vendors/${id}/`),
    merge: async (primaryId: number, secondaryVendorId: number) =>
      api.post(`vendors/${primaryId}/merge/`, { secondary_vendor_id: secondaryVendorId })
  },
  project: {
    create: async (data: { name: string; company_id: number; users?: number[] }) =>
      api.post<Project>('projects/', data),
    fetch: async (id: number) => api.get(`projects/${id}/`),
    update: async (id: number, data: ProjectToSendValues) => api.patch(`projects/${id}/`, data)
  },
  address: {
    fetch: async (id: number) => api.get<Address>(`addresses/${id}/`),
    create: async (data: Partial<Address>) => api.post<Address>('addresses/', data),
    update: async (id: number, data: Partial<Address>) => api.patch<Address>(`addresses/${id}/`, data)
  },
  docusign: {
    downloadTemplateDocument: async ({
      company_id,
      template_id,
      project_id
    }: {
      company_id: number;
      template_id: string;
      project_id?: number;
    }) =>
      api.get(`companies/${company_id}/get-template/`, {
        params: project_id ? { template_id, project_id } : { template_id },
        responseType: 'blob'
      }),
    deleteTemplateDocument: async ({
      company_id,
      template_id,
      project_id
    }: {
      company_id: number;
      template_id: string;
      project_id?: number;
    }) =>
      api.delete(`companies/${company_id}/delete-template/`, {
        params: project_id ? { template_id, project_id } : { template_id }
      })
  }
};

export default companiesAPI;
